"use client";

import * as signalR from "@microsoft/signalr";
import type {
  PokerTableView,
  ChatMessageDto,
  GameLogEntry,
  GameStartingDto,
  LastHandResultView,
  EndGameSummary,
  TournamentEliminationDto,
} from "./texas-holdem-types";
import { API_URL } from "@/lib/api-config";

export type LobbyHubEvents = {
  PlayerJoined: (player: { id: string; name: string; isHost: boolean }) => void;
  PlayerLeft: (playerId: string) => void;
  PlayerKicked: (playerId: string) => void;
  PlayerDisconnected: (playerId: string) => void;
  GameStarting: (data: GameStartingDto) => void;
  Error: (message: string) => void;
};

export type ReactionDto = {
  fromSeat: number;
  targetSeat: number;
  emoji: string;
};

export type TableHubEvents = {
  TableUpdated: (view: PokerTableView) => void;
  GameLog: (entry: GameLogEntry) => void;
  HandComplete: (result: LastHandResultView) => void;
  ChatMessage: (message: ChatMessageDto) => void;
  PlayerConnected: (playerId: string) => void;
  GameEnded: (summary: EndGameSummary) => void;
  ReceiveReaction: (reaction: ReactionDto) => void;
  PlayerEliminated: (elimination: TournamentEliminationDto) => void;
  Error: (message: string) => void;
};

type EventHandlerMap = Record<string, Set<(...args: unknown[]) => void>>;

function createConnection(hubUrl: string): signalR.HubConnection {
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(hubUrl, {
      skipNegotiation: false,
      transport: signalR.HttpTransportType.WebSockets,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  connection.serverTimeoutInMilliseconds = 120_000;
  connection.keepAliveIntervalInMilliseconds = 15_000;

  return connection;
}

export class PokerHubConnection<TEvents extends Record<string, (...args: never[]) => void>> {
  private connection: signalR.HubConnection | null = null;
  private handlers: EventHandlerMap = {};
  private connectionPromise: Promise<void> | null = null;

  constructor(private hubPath: string) {}

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) return;

    if (this.connectionPromise) return this.connectionPromise;

    if (this.connection) {
      try { this.connection.stop(); } catch {}
    }

    this.connection = createConnection(`${API_URL}${this.hubPath}`);

    for (const [event, handlerSet] of Object.entries(this.handlers)) {
      for (const handler of handlerSet) {
        this.connection.on(event, handler);
      }
    }

    this.connection.onreconnecting(() => {
      this.handlers["_reconnecting"]?.forEach((h) => h());
    });

    this.connection.onreconnected(() => {
      this.handlers["_reconnected"]?.forEach((h) => h());
    });

    this.connection.onclose(() => {
      this.handlers["_closed"]?.forEach((h) => h());
    });

    this.connectionPromise = Promise.race([
      this.connection.start(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Connection start timed out")), 10_000),
      ),
    ])
      .then(() => {
        this.connectionPromise = null;
      })
      .catch((err) => {
        this.connectionPromise = null;
        console.error("[PokerHub] Connection failed:", err);
        throw err;
      });

    return this.connectionPromise;
  }

  on<K extends string & keyof TEvents>(event: K, handler: TEvents[K]): void {
    if (!this.handlers[event]) this.handlers[event] = new Set();
    this.handlers[event].add(handler as unknown as (...args: unknown[]) => void);
    this.connection?.on(event, handler as unknown as (...args: unknown[]) => void);
  }

  off<K extends string & keyof TEvents>(event: K, handler: TEvents[K]): void {
    this.handlers[event]?.delete(handler as unknown as (...args: unknown[]) => void);
    this.connection?.off(event, handler as unknown as (...args: unknown[]) => void);
  }

  onReconnecting(handler: () => void): void {
    if (!this.handlers["_reconnecting"]) this.handlers["_reconnecting"] = new Set();
    this.handlers["_reconnecting"].add(handler);
  }

  onReconnected(handler: () => void): void {
    if (!this.handlers["_reconnected"]) this.handlers["_reconnected"] = new Set();
    this.handlers["_reconnected"].add(handler);
  }

  onClose(handler: () => void): void {
    if (!this.handlers["_closed"]) this.handlers["_closed"] = new Set();
    this.handlers["_closed"].add(handler);
  }

  async invoke<T = void>(method: string, ...args: unknown[]): Promise<T> {
    if (!this.connection) {
      throw new Error("Not connected");
    }

    if (this.connection.state === signalR.HubConnectionState.Reconnecting) {
      await this.waitForConnected(10_000);
    }

    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error("Not connected");
    }

    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Invoke timed out")), 15_000),
    );

    return Promise.race([this.connection.invoke<T>(method, ...args), timeout]);
  }

  private waitForConnected(timeoutMs: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.connection?.state === signalR.HubConnectionState.Connected) {
        resolve();
        return;
      }

      const cleanup = () => {
        clearTimeout(timer);
        this.handlers["_reconnected"]?.delete(onReconnected as unknown as (...args: unknown[]) => void);
        this.handlers["_closed"]?.delete(onClosed as unknown as (...args: unknown[]) => void);
      };

      const timer = setTimeout(() => {
        cleanup();
        reject(new Error("Connection timeout"));
      }, timeoutMs);

      const onReconnected = () => {
        cleanup();
        resolve();
      };

      const onClosed = () => {
        cleanup();
        reject(new Error("Connection closed"));
      };

      this.onReconnected(onReconnected);
      this.onClose(onClosed);
    });
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
    this.handlers = {};
    this.connectionPromise = null;
  }

  get state(): signalR.HubConnectionState {
    return this.connection?.state ?? signalR.HubConnectionState.Disconnected;
  }
}

export function createLobbyConnection(): PokerHubConnection<LobbyHubEvents> {
  return new PokerHubConnection<LobbyHubEvents>("/hubs/poker/lobby");
}

export function createTableConnection(): PokerHubConnection<TableHubEvents> {
  return new PokerHubConnection<TableHubEvents>("/hubs/poker/table");
}

export async function getVoiceToken(
  table: PokerHubConnection<TableHubEvents>
): Promise<string> {
  return table.invoke<string>("GetVoiceToken");
}
